import type { Lang } from '../page';

interface FeaturesSpecsProps { lang: Lang; }

type LM = Record<Lang, string>;

const specTitle: LM = {
  en: 'Technical Specifications', zh: '技术参数', th: 'ข้อมูลจำเพาะทางเทคนิค',
  vi: 'Thông Số Kỹ Thuật', ms: 'Spesifikasi Teknikal',
};
const specSub: LM = {
  en: '技术参数速览', zh: 'Technical Parameters Overview',
  th: 'ภาพรวมพารามิเตอร์ทางเทคนิค', vi: 'Tổng quan thông số kỹ thuật', ms: 'Gambaran Keseluruhan Parameter Teknikal',
};
const specDesc: LM = {
  en: 'Our polyimide films strictly comply with GB/T 13542, IEC 60674, UL 510 and other international standards. ISO 9001 certified, ensuring every batch meets the highest quality requirements.',
  zh: '我们的聚酰亚胺薄膜严格符合GB/T 13542、IEC 60674、UL 510等国际标准。通过ISO 9001认证，确保每批产品均达到最高质量要求。',
  th: 'ฟิล์มโพลีอิไมด์ของเราเป็นไปตาม GB/T 13542, IEC 60674, UL 510 และมาตรฐานสากลอื่นๆ ผ่านการรับรอง ISO 9001',
  vi: 'Màng polyimide của chúng tôi tuân thủ GB/T 13542, IEC 60674, UL 510 và các tiêu chuẩn quốc tế khác. Được chứng nhận ISO 9001.',
  ms: 'Filem polyimide kami mematuhi GB/T 13542, IEC 60674, UL 510 dan piawaian antarabangsa lain. Diperakui ISO 9001.',
};
const downloadBtn: LM = {
  en: 'Download Complete Datasheet', zh: '下载完整数据手册',
  th: 'ดาวน์โหลดข้อมูลจำเพาะฉบับสมบูรณ์', vi: 'Tải Xuống Thông Số Đầy Đủ', ms: 'Muat Turun Helaian Data Lengkap',
};
const appTitle: LM = {
  en: 'Wide Range of Applications', zh: '广泛应用领域', th: 'การใช้งานหลากหลาย',
  vi: 'Ứng Dụng Đa Dạng', ms: 'Pelbagai Aplikasi',
};
const appSub: LM = {
  en: '广泛应用领域', zh: 'Wide Range of Applications', th: 'Wide Range of Applications',
  vi: 'Ứng dụng đa dạng', ms: 'Aplikasi Pelbagai',
};

const specifications = [
  {
    category: { en: 'Physical Properties', zh: '物理性能', th: 'คุณสมบัติทางกายภาพ', vi: 'Tính Chất Vật Lý', ms: 'Sifat Fizikal' } as LM,
    params: [
      { name: { en: 'Density', zh: '密度', th: 'ความหนาแน่น', vi: 'Mật Độ', ms: 'Ketumpatan' } as LM, value: '1.42 g/cm³' },
      { name: { en: 'Tensile Strength', zh: '抗拉强度', th: 'ความแข็งแรงดึง', vi: 'Độ Bền Kéo', ms: 'Kekuatan Tegangan' } as LM, value: '≥170 MPa' },
      { name: { en: 'Elongation at Break', zh: '断裂伸长率', th: 'การยืดตัวก่อนขาด', vi: 'Độ Giãn Dài Khi Đứt', ms: 'Pemanjangan Semasa Patah' } as LM, value: '≥70%' },
      { name: { en: 'Tear Strength', zh: '撕裂强度', th: 'ความแข็งแรงการฉีกขาด', vi: 'Độ Bền Xé', ms: 'Kekuatan Koyak' } as LM, value: '≥80 N/mm' },
    ],
  },
  {
    category: { en: 'Electrical Properties', zh: '电气性能', th: 'คุณสมบัติทางไฟฟ้า', vi: 'Tính Chất Điện', ms: 'Sifat Elektrik' } as LM,
    params: [
      { name: { en: 'Dielectric Strength', zh: '介电强度', th: 'ความแข็งแรงทางไฟฟ้า', vi: 'Độ Bền Điện Môi', ms: 'Kekuatan Dielektrik' } as LM, value: '200 kV/mm' },
      { name: { en: 'Volume Resistivity', zh: '体积电阻率', th: 'ความต้านทานเชิงปริมาตร', vi: 'Điện Trở Suất Thể Tích', ms: 'Kerintangan Isipadu' } as LM, value: '≥10¹⁷ Ω·cm' },
      { name: { en: 'Dielectric Constant', zh: '介电常数', th: 'ค่าคงที่ไดอิเล็กทริก', vi: 'Hằng Số Điện Môi', ms: 'Pemalar Dielektrik' } as LM, value: '3.4 @1MHz' },
      { name: { en: 'Dissipation Factor', zh: '损耗因子', th: 'ปัจจัยการสูญเสีย', vi: 'Hệ Số Tiêu Tán', ms: 'Faktor Lesapan' } as LM, value: '0.002 @1MHz' },
    ],
  },
  {
    category: { en: 'Thermal Properties', zh: '热性能', th: 'คุณสมบัติทางความร้อน', vi: 'Tính Chất Nhiệt', ms: 'Sifat Terma' } as LM,
    params: [
      { name: { en: 'Glass Transition Temp', zh: '玻璃化转变温度', th: 'อุณหภูมิทรานสิชันแก้ว', vi: 'Nhiệt Độ Chuyển Tiếp Thủy Tinh', ms: 'Suhu Peralihan Kaca' } as LM, value: '360°C' },
      { name: { en: 'Continuous Use Temp', zh: '连续使用温度', th: 'อุณหภูมิใช้งานต่อเนื่อง', vi: 'Nhiệt Độ Sử Dụng Liên Tục', ms: 'Suhu Penggunaan Berterusan' } as LM, value: '260°C' },
      { name: { en: 'Short-term Heat Resistance', zh: '短期耐热性', th: 'ทนความร้อนระยะสั้น', vi: 'Chịu Nhiệt Ngắn Hạn', ms: 'Rintangan Haba Jangka Pendek' } as LM, value: '400°C' },
      { name: { en: 'Thermal Expansion Coeff', zh: '热膨胀系数', th: 'สัมประสิทธิ์การขยายตัวทางความร้อน', vi: 'Hệ Số Giãn Nở Nhiệt', ms: 'Pekali Pengembangan Terma' } as LM, value: '2×10⁻⁵ /K' },
    ],
  },
  {
    category: { en: 'Dimensions', zh: '尺寸规格', th: 'ขนาด', vi: 'Kích Thước', ms: 'Dimensi' } as LM,
    params: [
      { name: { en: 'Thickness Range', zh: '厚度范围', th: 'ช่วงความหนา', vi: 'Phạm Vi Độ Dày', ms: 'Julat Ketebalan' } as LM, value: '0.025-0.05 mm' },
      { name: { en: 'Width Range', zh: '宽度范围', th: 'ช่วงความกว้าง', vi: 'Phạm Vi Chiều Rộng', ms: 'Julat Lebar' } as LM, value: '12-1040 mm' },
      { name: { en: 'Thickness Tolerance', zh: '厚度公差', th: 'ความคลาดเคลื่อนความหนา', vi: 'Dung Sai Độ Dày', ms: 'Toleransi Ketebalan' } as LM, value: '±5%' },
      { name: { en: 'Surface Roughness', zh: '表面粗糙度', th: 'ความหยาบผิว', vi: 'Độ Nhám Bề Mặt', ms: 'Kekasaran Permukaan' } as LM, value: '≤0.5 μm' },
    ],
  },
];

const applications = [
  {
    title: { en: 'Aerospace', zh: '航空航天', th: 'การบินและอวกาศ', vi: 'Hàng Không Vũ Trụ', ms: 'Aeroangkasa' } as LM,
    desc:  { en: 'Aircraft cable insulation, satellite thermal control materials', zh: '飞机线缆绝缘、卫星热控制材料', th: 'ฉนวนสายเคเบิลอากาศยาน วัสดุควบคุมความร้อนดาวเทียม', vi: 'Cách điện dây cáp máy bay, vật liệu kiểm soát nhiệt vệ tinh', ms: 'Penebat kabel pesawat, bahan kawalan haba satelit' } as LM,
    image: 'https://public.readdy.ai/ai/img_res/cc566c35c0eaa38786931ffc263a587e.jpg',
  },
  {
    title: { en: 'Electronics', zh: '电子电器', th: 'อิเล็กทรอนิกส์', vi: 'Điện Tử', ms: 'Elektronik' } as LM,
    desc:  { en: 'Flexible PCB, motor insulation', zh: '柔性印刷电路板、电机绝缘', th: 'แผงวงจรแบบยืดหยุ่น ฉนวนมอเตอร์', vi: 'PCB linh hoạt, cách điện động cơ', ms: 'PCB fleksibel, penebat motor' } as LM,
    image: 'https://readdy.ai/api/search-image?query=High%20resolution%20macro%20photography%20of%20flexible%20printed%20circuit%20board%20made%20with%20golden%20transparent%20polyimide%20film%20substrate%20showing%20detailed%20copper%20traces%20and%20electronic%20components%20on%20amber%20colored%20flexible%20material%20in%20electronics%20manufacturing%20clean%20room%20with%20bright%20professional%20lighting&width=600&height=800&seq=app-electronics-real-pcb-001&orientation=portrait',
  },
  {
    title: { en: 'New Energy Vehicles', zh: '新能源汽车', th: 'ยานยนต์พลังงานใหม่', vi: 'Xe Năng Lượng Mới', ms: 'Kenderaan Tenaga Baharu' } as LM,
    desc:  { en: 'Battery insulation, motor protection', zh: '电池绝缘、电机保护', th: 'ฉนวนแบตเตอรี่ การป้องกันมอเตอร์', vi: 'Cách điện pin, bảo vệ động cơ', ms: 'Penebat bateri, perlindungan motor' } as LM,
    image: 'https://readdy.ai/api/search-image?query=Detailed%20close-up%20photography%20of%20polyimide%20film%20insulation%20layers%20in%20electric%20vehicle%20lithium%20battery%20pack%20showing%20transparent%20golden%20amber%20colored%20thin%20film%20material%20between%20battery%20cells%20providing%20electrical%20insulation%20and%20thermal%20protection%20in%20modern%20automotive%20manufacturing%20environment&width=600&height=800&seq=app-ev-real-battery-001&orientation=portrait',
  },
  {
    title: { en: 'Industrial Manufacturing', zh: '工业制造', th: 'การผลิตอุตสาหกรรม', vi: 'Sản Xuất Công Nghiệp', ms: 'Pembuatan Industri' } as LM,
    desc:  { en: 'High-temperature tape, protective materials', zh: '高温胶带、防护材料', th: 'เทปทนอุณหภูมิสูง วัสดุป้องกัน', vi: 'Băng keo chịu nhiệt, vật liệu bảo vệ', ms: 'Pita suhu tinggi, bahan perlindungan' } as LM,
    image: 'https://readdy.ai/api/search-image?query=Professional%20product%20photography%20of%20high%20temperature%20polyimide%20adhesive%20tape%20roll%20showing%20golden%20amber%20transparent%20film%20material%20with%20adhesive%20backing%20being%20applied%20to%20industrial%20machinery%20components%20in%20manufacturing%20facility%20with%20warm%20professional%20lighting&width=600&height=800&seq=app-industrial-real-tape-001&orientation=portrait',
  },
];

export default function FeaturesSpecs({ lang }: FeaturesSpecsProps) {
  return (
    <>
      <section id="specs" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-5xl lg:text-6xl font-bold">{specTitle[lang]}</h2>
              <p className="text-2xl text-gray-400">{specSub[lang]}</p>
              <p className="text-lg text-gray-300 leading-relaxed">{specDesc[lang]}</p>
              <a href="#download" className="inline-flex items-center space-x-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap">
                <i className="ri-download-cloud-line text-xl"></i>
                <span>{downloadBtn[lang]}</span>
              </a>
            </div>

            <div className="space-y-6">
              {specifications.map((spec, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-amber-400 mb-1">{spec.category[lang]}</h3>
                  <div className="space-y-3 mt-4">
                    {spec.params.map((param, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-gray-300">{param.name[lang]}</span>
                        <div className="flex-1 mx-4 border-b border-dotted border-gray-600"></div>
                        <span className="text-amber-400 font-bold">{param.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="applications" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-4">{appTitle[lang]}</h2>
            <p className="text-xl text-gray-500">{appSub[lang]}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {applications.map((app, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-3xl mb-4 aspect-[3/4]">
                  <img src={app.image} alt={app.title.en} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">{app.title[lang]}</h3>
                <p className="text-gray-600 text-center text-sm">{app.desc[lang]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
